import React from "react";
import { MapPin, Clock, Phone, Mail, Instagram, MessageCircle } from "lucide-react";
import { useLang } from "@/lib/i18n";

function Soho69Map() {
  return (
    <iframe
      title="Soho69 Tenerife"
      data-testid="contact-map"
      src="https://www.google.com/maps?q=Soho69+Tenerife&output=embed"
      className="w-full h-[420px] md:h-[520px] border-0"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  );
}

function TikTokIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 22}
      height={props.size || 22}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={props.className}
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
    </svg>
  );
}

export default function Contact() {
  const { t } = useLang();
  return (
    <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-4 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32 overflow-x-hidden">
      <h1 className="sr-only">{t("contact.overline")} — {t("contact.title.1")} {t("contact.title.2")}</h1>
      <img
        src="/banners/find-us.png"
        alt="Get in Touch — Find Us"
        data-testid="contact-hero-banner"
        loading="eager"
        decoding="async"
        className="block w-full max-w-[900px] h-auto -ml-2 md:-ml-4"
      />
      <p className="text-white/60 mt-4 max-w-xl text-sm md:text-base">{t("contact.sub")}</p>

      <div className="mt-12 md:mt-14 grid lg:grid-cols-2 gap-8 md:gap-10">
        <div className="space-y-3 md:space-y-4">
          <Card icon={MapPin} title={t("contact.card.address")}>
            {t("contact.address.lines").split("\n").map((ln, i) => (
              <React.Fragment key={i}>{ln}<br /></React.Fragment>
            ))}
            <a
              href="https://maps.google.com/?q=Soho69+Tenerife"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-maps-cta"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-neon text-neon text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-neon/10 transition"
            >
              📍 Open in Google Maps →
            </a>
          </Card>
          <Card icon={Clock} title={t("contact.card.hours")}>
            {t("contact.hours.lines").split("\n").map((ln, i) => (
              <React.Fragment key={i}>{ln}<br /></React.Fragment>
            ))}
          </Card>
          <Card icon={Phone} title={t("contact.card.phone")}>+34 611 37 97 36</Card>
          <Card icon={MessageCircle} title={t("contact.card.whatsapp")}>+34 611 37 97 36</Card>
          <Card icon={Mail} title={t("contact.card.email")}>
            <a
              href="mailto:soho69tenerife@gmail.com"
              className="text-white hover:text-neon transition whitespace-nowrap"
              data-testid="contact-email"
            >
              soho69tenerife@gmail.com
            </a>
          </Card>
          <Card icon={Instagram} title={t("contact.card.instagram")}>@soho69tfs</Card>
          <Card icon={TikTokIcon} title={t("contact.card.tiktok")}>@soho69tenerie</Card>
        </div>

        <div className="rounded-4xl overflow-hidden surface relative">
          <Soho69Map />
        </div>
      </div>

      <div className="mt-14 md:mt-16 rounded-4xl border border-neon/30 bg-neon/5 p-6 md:p-8">
        <h3 className="font-heading text-2xl md:text-3xl uppercase">{t("contact.rules.title")}</h3>
        <ul className="mt-4 space-y-2 text-white/80 text-sm list-disc list-inside">
          <li>{t("contact.rules.1")}</li>
          <li>{t("contact.rules.2")}</li>
          <li>{t("contact.rules.3")}</li>
          <li>{t("contact.rules.4")}</li>
          <li>{t("contact.rules.5")}</li>
          <li>{t("contact.rules.6")}</li>
        </ul>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="surface rounded-4xl p-5 md:p-6 flex gap-4 hover:border-neon/50 transition">
      <Icon className="text-neon shrink-0 mt-1" size={22} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{title}</div>
        <div className="text-white mt-1 leading-relaxed break-words">{children}</div>
      </div>
    </div>
  );
}
